import { useContext, useState } from "react";
import { FaLinkedinIn, FaEnvelope, FaPhone, FaLocationDot } from "react-icons/fa6";
import { LuArrowUpRight, LuArrowUp, LuPlus } from "react-icons/lu";
import { emailaddress, emailRegex, aiServicePages, company } from "../controller/global";
import { GlobalStateContext } from "../controller/GlobalState";
import { Link } from "react-router";
import logo from "../assets/images/inqmise-wordmark.png";
import cledgeLogo from "../assets/images/cledge/cledge-wordmark-white.png";
import kraftigoLogo from "../assets/images/kraftigo/kraftigo-wordmark.png";
import vettingoLogo from "../assets/images/vetingo/vetingo-logo.png";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const { setIsOpen, setFormData } = useContext(GlobalStateContext);
  const productFooters = [
    { path: "/products/cledge", name: "Cledge", logo: cledgeLogo, logoClass: "sf-product-logo-cledge", description: "Clinic management software for appointments, patient records and multiple branches." },
    { path: "/products/kraftigo", name: "Kraftigo", logo: kraftigoLogo, logoClass: "sf-product-logo-kraftigo", description: "Connected sales, production planning and inventory for corrugated packaging operations." },
    { path: "/products/vettingo", name: "Vettingo", logo: vettingoLogo, logoClass: "sf-product-logo-vettingo", description: "Connected talent workflows for structured, explainable candidate review." },
  ];

  const handleSubmit = (event) => {
    event.preventDefault();
    const value = email.trim();
    if (!value || !emailRegex.test(value.toLowerCase())) {
      setError(value ? "Enter a valid email address." : "Email is required.");
      return;
    }
    setFormData((previous) => ({ ...previous, email: value }));
    setError("");
    setIsOpen(true);
  };

  const scrollToTop = () => window.scrollTo({
    top: 0,
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
  });

  const navLinks = [
    { label: "Home", path: "/" },
    { label: "About", path: "/about" },
    { label: "Technologies", path: "/technologies" },
    { label: "Services", path: "/services" },
    { label: "Career", path: "/career" },
    { label: "Contact", path: "/contact" },
    { label: "Case Studies", path: "/case-studies" },
    { label: "How we work", path: "/how-we-work-and-function" },
    { label: "FAQ", path: "/faq" },
    // Were in the sitemap with almost nothing linking to them — indexable orphans.
    { label: "Our Team", path: "/our-core-team" },
    { label: "Responsibility", path: "/corporate-social-responsibility" },
  ];

  // Only profiles that actually exist belong here. Instagram, Facebook and X
  // were placeholder icons pointing at href="#", which reloaded the page on
  // every click. Add an entry back when the account is live.
  const deliveryPages = [
    { title: "SaaS Development", path: "/saas-development" },
    { title: "CRM Development", path: "/crm-development" },
    { title: "Cloud Migration", path: "/cloud-migration" },
    { title: "Software Outsourcing", path: "/software-development-outsourcing" },
    { title: "Offshore Development", path: "/offshore-software-development" },
  ];

  // The rest of the service catalogue. The columns above only reach the AI
  // pages and five engagement pages, so most service pages had no footer link
  // at all (SEO plan, section 6). Link text is each page's target keyword.
  const serviceDirectory = [
    {
      title: "Build",
      links: [
        { title: "Web development", path: "/web-development" },
        { title: "Mobile app development", path: "/mobile-app-development" },
        { title: "iOS app development", path: "/ios-development" },
        { title: "Android app development", path: "/android-development" },
        { title: "Cross-platform apps", path: "/native-cross-platform-apps" },
        { title: "Mobile app consulting", path: "/mobile-consulting" },
        { title: "Mobile-first design", path: "/mobile-first-design" },
        { title: "Mobile performance & security", path: "/mobile-performance-security" },
        { title: "UI/UX design", path: "/ui-ux-design" },
        { title: "Ecommerce development", path: "/ecommerce-development" },
        { title: "API development", path: "/api-development" },
        { title: "Custom ERP software", path: "/erp-services" },
        { title: "IoT development", path: "/iot" },
        { title: "UX research", path: "/user-research" },
        { title: "Wireframing & prototyping", path: "/wireframing-prototyping" },
        { title: "Web performance", path: "/performance-optimization" },
      ],
    },
    {
      title: "Cloud & security",
      links: [
        { title: "Cloud development", path: "/cloud-development" },
        { title: "Cloud native applications", path: "/cloud-native-applications" },
        { title: "Cloud consulting", path: "/cloud-consulting" },
        { title: "DevOps services", path: "/devops-services" },
        { title: "CI/CD pipelines", path: "/ci-cd-pipelines" },
        { title: "Cybersecurity & compliance", path: "/cybersecurity-compliance" },
        { title: "VAPT services", path: "/security-audits-pen-testing" },
        { title: "Cloud security", path: "/cloud-security-facet" },
        { title: "Cloud architecture design", path: "/cloud-architecture-design" },
        { title: "Infrastructure automation", path: "/cloud-infrastructure-automation" },
        { title: "Serverless & microservices", path: "/serverless-microservices" },
        { title: "Cybersecurity consulting", path: "/cybersecurity-consulting-services" },
        { title: "SOC 2, ISO 27001 & GDPR", path: "/compliance-regulations" },
      ],
    },
    {
      title: "Consulting & support",
      links: [
        { title: "IT consulting", path: "/it-consulting" },
        { title: "Digital transformation", path: "/digital-transformation" },
        { title: "Business intelligence", path: "/business-intelligence" },
        { title: "Data analytics", path: "/data-analytics" },
        { title: "Big data services", path: "/big-data" },
        { title: "Software integration", path: "/software-integration-services" },
        { title: "Legacy modernization", path: "/legacy-system-modernization" },
        { title: "Software testing & QA", path: "/qa-and-testing" },
        { title: "Managed IT services", path: "/managed-it-services" },
        { title: "Support & maintenance", path: "/product-support-maintenance" },
        { title: "Software consulting", path: "/software-consulting" },
        { title: "IT audit services", path: "/it-audit-services" },
        { title: "Fintech consulting", path: "/fintech-consulting" },
        { title: "Transformation roadmap", path: "/digital-transformation-roadmap" },
        { title: "Updates & bug fixes", path: "/updates-bug-fixes" },
        { title: "Support & maintenance plans", path: "/support-maintenance" },
        { title: "Blockchain consulting", path: "/blockchain" },
      ],
    },
  ];

  return (
    <footer className="site-footer">
      <div className="sf-wrap">
        <div className="sf-invitation">
          <div>
            <span className="sf-kicker">Good things start with a conversation.</span>
            <h2>Ready for your <span>next build?</span></h2>
          </div>
          <button className="sf-project" onClick={() => setIsOpen(true)}>
            <span>Discuss your project</span><span className="sf-project-arrow"><LuArrowUpRight aria-hidden="true" /></span>
          </button>
        </div>

        <div className="sf-main">
          <div className="sf-brand">
            <Link to="/" className="sf-logo" aria-label="InQmise Technologies home">
              <img src={logo} alt="InQmise Technologies" width="502" height="98" loading="lazy" />
            </Link>
            <p>Software, AI and cloud solutions built around your business. From the first conversation to what comes next.</p>
            <span className="sf-location"><span aria-hidden="true" />Delhi NCR · Working worldwide</span>
            <a className="sf-social" href="https://www.linkedin.com/company/inqmise-technologies" target="_blank" rel="noopener noreferrer">
              <FaLinkedinIn aria-hidden="true" /><span>Follow us on LinkedIn</span><LuArrowUpRight aria-hidden="true" />
            </a>
          </div>

          <nav className="sf-column" aria-label="Footer company navigation">
            <h3>Explore InQmise</h3>
            <ul>{navLinks.map((item) => <li key={item.path}><Link to={item.path}>{item.label}</Link></li>)}</ul>
          </nav>

          <nav className="sf-column" aria-label="Footer expertise navigation">
            <h3>Our expertise</h3>
            <ul>
              {aiServicePages.map((item) => <li key={item.path}><Link to={item.path}>{item.title}</Link></li>)}
              {deliveryPages.map((item) => <li key={item.path}><Link to={item.path}>{item.title}</Link></li>)}
              <li><Link className="sf-all-link" to="/services">View all services <LuArrowUpRight aria-hidden="true" /></Link></li>
            </ul>
          </nav>

          <div className="sf-contact">
            <h3>Let’s talk</h3>
            <a className="sf-contact-link" href={`mailto:${emailaddress}`}><FaEnvelope aria-hidden="true" /><span>{emailaddress}</span></a>
            <a className="sf-contact-link" href={`tel:${company.phoneHref}`}><FaPhone aria-hidden="true" /><span>{company.phone}</span></a>
            <address><FaLocationDot aria-hidden="true" /><span>{company.street},<br />{company.locality}, {company.region} {company.postalCode}, {company.country}</span></address>
            <form className="sf-email-form" onSubmit={handleSubmit} noValidate>
              <label htmlFor="footer-email">Start with your email</label>
              <div className={`sf-email-field${error ? " sf-email-invalid" : ""}`}>
                <input id="footer-email" type="email" name="email" autoComplete="email" placeholder="Your email address" value={email}
                  onChange={(event) => { setEmail(event.target.value); setError(""); }} aria-invalid={Boolean(error)} aria-describedby={error ? "footer-email-error" : "footer-email-note"} />
                <button type="submit" aria-label="Continue to the project inquiry form"><LuArrowUpRight aria-hidden="true" /></button>
              </div>
              {error && <p className="sf-error" id="footer-email-error" role="alert">{error}</p>}
              <p className="sf-email-note" id="footer-email-note">Continue to our project inquiry form.</p>
            </form>
          </div>
        </div>

        <section className="sf-product" aria-labelledby="footer-products-heading">
          <div className="sf-product-heading"><h3 id="footer-products-heading">Our Products</h3><span>Explore the tools built by InQmise</span></div>
          <div className="sf-products-grid">
            {productFooters.map((product) => <Link key={product.path} to={product.path} className="sf-product-card" aria-label={`Explore ${product.name}`}>
              <span className="sf-product-link"><img className={product.logoClass} src={product.logo} alt={product.name} loading="lazy" /><LuArrowUpRight aria-hidden="true" /></span>
              <span className="sf-product-description">{product.description}</span>
            </Link>)}
          </div>
        </section>

        <details className="sf-directory">
          <summary><span>Explore our complete service directory</span><LuPlus className="sf-directory-toggle" aria-hidden="true" /></summary>
          <nav className="sf-directory-grid" aria-label="All services">
            {serviceDirectory.map((group) => (
              <div key={group.title}><h3>{group.title}</h3><ul>{group.links.map((item) => <li key={item.path}><Link to={item.path}>{item.title}</Link></li>)}</ul></div>
            ))}
          </nav>
        </details>

        <div className="sf-bottom">
          <p>© {new Date().getFullYear()} InQmise Technologies. All rights reserved.</p>
          <div className="sf-bottom-links"><Link to="/contact">Contact us</Link><button onClick={scrollToTop}>Back to top <LuArrowUp aria-hidden="true" /></button></div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
